(() => {
  const STORAGE_KEY = 'poster-store:wishlist:v1';
  const MAX_ITEMS = 50;
  let items = [];
  let returnFocus = null;

  const drawer = () => document.querySelector('[data-wishlist-drawer]');

  const readItems = () => {
    try {
      const stored = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
      if (!Array.isArray(stored)) throw new TypeError('Wishlist data is not an array.');
      return stored.filter((item) => item && typeof item.handle === 'string' && typeof item.url === 'string');
    } catch (error) {
      console.warn('Favorites could not be read.', error);
      return [];
    }
  };

  const writeItems = () => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items.slice(0, MAX_ITEMS)));
    } catch (error) {
      console.warn('Favorites could not be saved.', error);
    }
  };

  const productFromButton = (button) => ({
    handle: button.dataset.productHandle || '',
    title: button.dataset.productTitle || button.dataset.productHandle || '',
    url: button.dataset.productUrl || '',
    image: button.dataset.productImage || '',
    price: button.dataset.productPrice || ''
  });

  const isFavorite = (handle) => items.some((item) => item.handle === handle);

  const updateToggle = (button) => {
    const pressed = isFavorite(button.dataset.productHandle);
    button.setAttribute('aria-pressed', String(pressed));
    button.setAttribute('aria-label', pressed ? button.dataset.removeLabel : button.dataset.addLabel);
  };

  const syncToggles = (scope = document) => {
    scope.querySelectorAll('[data-wishlist-toggle]').forEach(updateToggle);
  };

  const updateCounts = () => {
    document.querySelectorAll('[data-wishlist-count]').forEach((count) => {
      count.textContent = String(items.length);
      count.hidden = items.length === 0;
    });
    document.querySelectorAll('[data-wishlist-drawer-count]').forEach((count) => {
      count.textContent = `(${items.length})`;
    });
  };

  const createWishlistItem = (item) => {
    const article = document.createElement('article');
    article.className = 'wishlist-item';

    const link = document.createElement('a');
    link.className = 'wishlist-item__media';
    link.href = item.url;
    if (item.image) {
      const image = document.createElement('img');
      image.src = item.image;
      image.alt = item.title;
      image.loading = 'lazy';
      image.width = 160;
      image.height = 200;
      link.append(image);
    }

    const content = document.createElement('div');
    content.className = 'wishlist-item__content';
    const title = document.createElement('a');
    title.className = 'wishlist-item__title';
    title.href = item.url;
    title.textContent = item.title;
    content.append(title);

    if (item.price) {
      const price = document.createElement('strong');
      price.textContent = item.price;
      content.append(price);
    }

    const remove = document.createElement('button');
    remove.className = 'wishlist-item__remove';
    remove.type = 'button';
    remove.dataset.wishlistRemove = item.handle;
    remove.textContent = 'Verwijderen';
    content.append(remove);
    article.append(link, content);
    return article;
  };

  const render = () => {
    const root = drawer();
    if (!root) return;
    const list = root.querySelector('[data-wishlist-items]');
    const empty = root.querySelector('[data-wishlist-empty]');
    list.replaceChildren(...items.map(createWishlistItem));
    list.hidden = items.length === 0;
    empty.hidden = items.length > 0;
    updateCounts();
    syncToggles();
  };

  const announce = (message) => {
    const status = drawer()?.querySelector('[data-wishlist-status]');
    if (status) status.textContent = message;
  };

  const toggle = (button) => {
    const product = productFromButton(button);
    if (!product.handle || !product.url) return;
    if (isFavorite(product.handle)) {
      items = items.filter((item) => item.handle !== product.handle);
      announce(`${product.title} is verwijderd uit je favorieten.`);
    } else {
      items = [product, ...items.filter((item) => item.handle !== product.handle)].slice(0, MAX_ITEMS);
      announce(`${product.title} is toegevoegd aan je favorieten.`);
    }
    writeItems();
    render();
  };

  const openDrawer = (trigger) => {
    const root = drawer();
    if (!root) return;
    returnFocus = trigger;
    root.dataset.open = '';
    root.setAttribute('aria-hidden', 'false');
    document.body.classList.add('wishlist-open');
    document.querySelectorAll('[data-wishlist-open]').forEach((button) => button.setAttribute('aria-expanded', 'true'));
    root.querySelector('.wishlist-drawer__panel')?.focus();
  };

  const closeDrawer = () => {
    const root = drawer();
    if (!root) return;
    root.removeAttribute('data-open');
    root.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('wishlist-open');
    document.querySelectorAll('[data-wishlist-open]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
    returnFocus?.focus();
  };

  const migrateVisibleLegacyFavorites = () => {
    document.querySelectorAll('[data-wishlist-toggle]').forEach((button) => {
      const product = productFromButton(button);
      if (!product.handle) return;
      try {
        const legacyKey = `poster-store-favorite:${product.handle}`;
        if (window.localStorage.getItem(legacyKey) === 'true' && !isFavorite(product.handle)) items.push(product);
        window.localStorage.removeItem(legacyKey);
      } catch (error) {
        console.warn('A previous favorite could not be migrated.', error);
      }
    });
    items = items.slice(0, MAX_ITEMS);
    writeItems();
  };

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const toggleButton = target.closest('[data-wishlist-toggle]');
    if (toggleButton) {
      event.preventDefault();
      toggle(toggleButton);
      return;
    }

    const openButton = target.closest('[data-wishlist-open]');
    if (openButton) {
      openDrawer(openButton);
      return;
    }

    if (target.closest('[data-wishlist-close]')) {
      closeDrawer();
      return;
    }

    const removeButton = target.closest('[data-wishlist-remove]');
    if (removeButton) {
      const removed = items.find((item) => item.handle === removeButton.dataset.wishlistRemove);
      items = items.filter((item) => item.handle !== removeButton.dataset.wishlistRemove);
      writeItems();
      render();
      if (removed) announce(`${removed.title} is verwijderd uit je favorieten.`);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && drawer()?.hasAttribute('data-open')) closeDrawer();
  });

  const boot = () => {
    items = readItems();
    migrateVisibleLegacyFavorites();
    render();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('wishlist:refresh', (event) => syncToggles(event.detail?.scope || document));
  document.addEventListener('shopify:section:load', (event) => {
    syncToggles(event.target);
    render();
  });
})();
